import React, { useCallback, useMemo, useState } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import {
  Bar, Brush, CartesianGrid, Cell, ComposedChart, Line, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis
} from 'recharts';
import {
  AXIS_TICK, BORDER, ChartCard, ChartSkeleton, NoData, SeriesLegend, SeriesToggle, ShareBar, Stat, StatStrip, SubText, TABLE_SX, TipBox, TipRow,
  TooltipInjected, tooltipPoint
} from '../../OnlineJournal/components/analytics/chartKit';
import { CHART } from '../../OnlineJournal/constants';
import { fmtInt, fmtPct, share } from '../../OnlineJournal/format';
import { useLoad } from '../../OnlineJournal/hooks/useLoad';
import { WATCH_COLORS } from '../constants';
import { plural } from '../format';
import { DocumentFilters, WatchAction, WatchKind } from '../types';
import { analyticsApi } from './analyticsApi';
import { bucketLabel, bucketTitle, dm, EVENT_COLORS, KIND_LABELS, kindPatch } from './analyticsFormat';
import { BRUSH_FROM, ByToggle, HEIGHT, toggleIn, useBucketBy } from './analyticsKit';
import { BucketBy, CleanupBucket, CleanupResponse, ViewProps } from './analyticsTypes';

/** Статусы, в которых автоочистка отменяет документ, и статусы, в которых скрывает (как на сервере) */
const ACTION_STATUSES: Record<WatchAction, string[]> = {
  cancel: ['6', '7', '13', '15'],
  hide: ['9', '11', '12', '14']
};
const ACTIONS: Array<{ action: WatchAction; label: string }> = [
  { action: 'cancel', label: 'Ждут автоотмены' },
  { action: 'hide', label: 'Ждут автоскрытия' }
];
const KINDS: WatchKind[] = ['direct', 'via', 'return'];

const SERIES: SeriesToggle[] = [
  { key: 'autoCancel', label: 'Автоотмена', color: EVENT_COLORS.autoCancel },
  { key: 'autoHide', label: 'Автоскрытие', color: EVENT_COLORS.autoHide },
  { key: 'extended', label: 'Продлено вручную', color: EVENT_COLORS.extended, line: true }
];

type OnWarnings = (extra?: Partial<DocumentFilters>) => void;

const Tip: React.FC<TooltipInjected<CleanupBucket> & { by?: BucketBy }> = props => {
  const p = tooltipPoint(props);
  if (!p) return null;
  const rows: TipRow[] = [
    { label: 'Автоотменено', value: fmtInt(p.autoCancel), color: EVENT_COLORS.autoCancel },
    { label: 'Автоскрыто', value: fmtInt(p.autoHide), color: EVENT_COLORS.autoHide },
    { label: 'Всего автоочисткой', value: fmtInt(p.autoCancel + p.autoHide), bold: true },
    { label: 'Продлено вручную', value: fmtInt(p.extended), color: EVENT_COLORS.extended, divider: true, hint: 'документов, по которым продлили срок' }
  ];
  return <TipBox width={270} title={bucketTitle(p.date, props.by || 'day', p.partial)} rows={rows} />;
};

const Chart: React.FC<{ data: CleanupResponse }> = ({ data }) => {
  const [hidden, setHidden] = useState<string[]>([]);
  const toggle = useCallback((key: string) => setHidden(h => toggleIn(h, key)), []);
  const show = (key: string) => hidden.indexOf(key) === -1;
  const points = data.buckets;

  if (!points.some(p => p.autoCancel || p.autoHide || p.extended)) {
    return <NoData height={220} text="За период автоочистка не срабатывала и сроки не продлевали" />;
  }
  return (
    <Box>
      <SeriesLegend items={SERIES} hidden={hidden} onToggle={toggle} />
      <Box sx={{ height: HEIGHT - 40 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={points} margin={{ top: 8, right: 8, left: 4, bottom: 0 }}>
            <CartesianGrid stroke={CHART.grid} vertical={false} />
            <XAxis dataKey="date" tick={AXIS_TICK} tickFormatter={d => dm(String(d))} axisLine={{ stroke: BORDER }} tickLine={false} minTickGap={14} />
            <YAxis tick={AXIS_TICK} tickFormatter={v => fmtInt(Number(v))} axisLine={false} tickLine={false} width={48} allowDecimals={false} />
            <ChartTooltip content={<Tip by={data.by} />} cursor={{ fill: 'rgba(148,163,184,0.12)' }} />
            {SERIES.filter(s => !s.line && show(s.key)).map(s => (
              <Bar key={s.key} dataKey={s.key} stackId="c" fill={s.color} isAnimationActive={false} maxBarSize={40}>
                {points.map(p => <Cell key={p.date} fillOpacity={p.partial ? 0.55 : 1} />)}
              </Bar>
            ))}
            {show('extended') && (
              <Line dataKey="extended" type="monotone" stroke={EVENT_COLORS.extended} strokeWidth={2} dot={points.length <= 31 ? { r: 2.5 } : false} isAnimationActive={false} />
            )}
            {points.length > BRUSH_FROM && (
              <Brush key={points.length} dataKey="date" height={22} stroke="#94a3b8" travellerWidth={8} startIndex={0} tickFormatter={d => dm(String(d))} />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

interface NowCell { watched: number; warnings: number; critical: number }
const EMPTY_NOW: NowCell = { watched: 0, warnings: 0, critical: 0 };

/** Ячейка «сейчас»: всего под наблюдением, ниже - на контроле и критично; клик открывает документы на контроле */
const NowValue: React.FC<{ cell: NowCell; onOpen: () => void }> = ({ cell, onOpen }) => (
  <Box
    onClick={cell.warnings ? onOpen : undefined}
    sx={{ cursor: cell.warnings ? 'pointer' : 'default', borderRadius: 1, px: 0.5, mx: -0.5, '&:hover': cell.warnings ? { bgcolor: '#fff7ed' } : undefined }}
    title={cell.warnings ? 'Открыть документы на контроле' : undefined}
  >
    <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: cell.watched ? '#0f172a' : '#cbd5e1' }}>{cell.watched ? fmtInt(cell.watched) : '-'}</Typography>
    {cell.watched > 0 && (
      <SubText color={cell.warnings ? WATCH_COLORS.warn : '#94a3b8'}>
        на контроле {fmtInt(cell.warnings)}{cell.critical ? ` · критично ${fmtInt(cell.critical)}` : ''}
      </SubText>
    )}
  </Box>
);

const KindsTable: React.FC<{ data: CleanupResponse; onWarnings: OnWarnings }> = ({ data, onWarnings }) => {
  const nowOf = (kind: WatchKind | null, action: WatchAction): NowCell => data.now
    .filter(r => r.action === action && (kind === null || r.kind === kind))
    .reduce<NowCell>((s, r) => ({ watched: s.watched + r.watched, warnings: s.warnings + r.warnings, critical: s.critical + r.critical }), EMPTY_NOW);
  const open = (kind: WatchKind | null, action: WatchAction) => () => onWarnings({ ...(kind ? kindPatch(kind) : {}), statuses: ACTION_STATUSES[action] });
  const rows = KINDS.map(kind => data.kinds.find(k => k.kind === kind) || { kind, autoCancel: 0, autoHide: 0, extended: 0 });

  return (
    <ChartCard title="По видам документов" subtitle="Слева - события за период, справа - что сейчас под наблюдением автоочистки (без учёта периода). Клик по «на контроле» - открыть эти документы.">
      <TableContainer sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5 }}>
        <Table size="small" sx={TABLE_SX}>
          <TableHead>
            <TableRow>
              <TableCell>Вид</TableCell>
              <TableCell align="right">Автоотменено</TableCell>
              <TableCell align="right">Автоскрыто</TableCell>
              <TableCell align="right">Продлено</TableCell>
              {ACTIONS.map(a => <TableCell key={a.action} align="right" sx={{ borderLeft: `1px solid ${BORDER}` }}>{a.label}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.kind}>
                <TableCell>{KIND_LABELS[r.kind]}</TableCell>
                <TableCell align="right">{fmtInt(r.autoCancel)}</TableCell>
                <TableCell align="right">{fmtInt(r.autoHide)}</TableCell>
                <TableCell align="right">{fmtInt(r.extended)}</TableCell>
                {ACTIONS.map(a => (
                  <TableCell key={a.action} align="right" sx={{ borderLeft: `1px solid ${BORDER}` }}>
                    <NowValue cell={nowOf(r.kind, a.action)} onOpen={open(r.kind, a.action)} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
            <TableRow sx={{ '& td': { fontWeight: 700, borderBottom: 0 } }}>
              <TableCell>Итого</TableCell>
              <TableCell align="right">{fmtInt(data.totals.autoCancel)}</TableCell>
              <TableCell align="right">{fmtInt(data.totals.autoHide)}</TableCell>
              <TableCell align="right">{fmtInt(data.totals.extended)}</TableCell>
              {ACTIONS.map(a => (
                <TableCell key={a.action} align="right" sx={{ borderLeft: `1px solid ${BORDER}` }}>
                  <NowValue cell={nowOf(null, a.action)} onOpen={open(null, a.action)} />
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </ChartCard>
  );
};

const OperatorsTable: React.FC<{ data: CleanupResponse }> = ({ data }) => {
  const max = data.operators.reduce((m, o) => Math.max(m, o.documents), 0);
  return (
    <ChartCard
      title="Кто продлевает сроки"
      subtitle={data.totals.operators > data.operators.length
        ? `Первые ${data.operators.length} из ${fmtInt(data.totals.operators)} по числу документов.`
        : 'Ручные продления срока автоочистки за период по документам вашей зоны.'}
    >
      {!data.operators.length ? <NoData height={160} text="За период сроки не продлевали" /> : (
        <TableContainer sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, maxHeight: 420 }}>
          <Table size="small" stickyHeader sx={TABLE_SX}>
            <TableHead>
              <TableRow>
                <TableCell>Пользователь</TableCell>
                <TableCell align="right">Продлений</TableCell>
                <TableCell align="right">Документов</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.operators.map(o => (
                <TableRow key={o.username}>
                  <TableCell sx={{ maxWidth: 240 }}>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }} noWrap title={o.fullName || o.username}>{o.fullName || o.username}</Typography>
                    {o.fullName && <SubText>{o.username}</SubText>}
                  </TableCell>
                  <TableCell align="right">{fmtInt(o.actions)}</TableCell>
                  <TableCell align="right" sx={{ minWidth: 130 }}>
                    <ShareBar value={o.documents} max={max} color={EVENT_COLORS.extended} label={fmtInt(o.documents)} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </ChartCard>
  );
};

function buildStats(data: CleanupResponse): Stat[] {
  const t = data.totals;
  const cleaned = t.autoCancel + t.autoHide;
  const now = data.now.reduce<NowCell>((s, r) => ({ watched: s.watched + r.watched, warnings: s.warnings + r.warnings, critical: s.critical + r.critical }), EMPTY_NOW);
  const peak = data.buckets.reduce<CleanupBucket | null>((b, p) => (!b || p.autoCancel + p.autoHide > b.autoCancel + b.autoHide ? p : b), null);
  return [
    { label: 'Автоотменено', value: fmtInt(t.autoCancel), color: t.autoCancel ? EVENT_COLORS.autoCancel : undefined, hint: cleaned ? `${fmtPct(share(t.autoCancel, cleaned))} автоочистки` : undefined },
    { label: 'Автоскрыто', value: fmtInt(t.autoHide), color: t.autoHide ? '#b45309' : undefined, hint: cleaned ? `${fmtPct(share(t.autoHide, cleaned))} автоочистки` : undefined },
    {
      label: 'Пик автоочистки', value: peak && peak.autoCancel + peak.autoHide ? fmtInt(peak.autoCancel + peak.autoHide) : '-',
      hint: peak && peak.autoCancel + peak.autoHide ? bucketLabel(peak.date, data.by) : 'не срабатывала'
    },
    {
      label: 'Продлено вручную', value: fmtInt(t.extended), color: t.extended ? EVENT_COLORS.extended : undefined,
      hint: `${fmtInt(t.actions)} ${plural(t.actions, 'действие', 'действия', 'действий')} · ${fmtInt(t.operators)} польз.`,
      tooltip: 'Сколько документов получили продление срока автоочистки; одно действие может продлить несколько документов'
    },
    { label: 'Сейчас под наблюдением', value: fmtInt(now.watched), hint: 'ждут автоочистки', tooltip: 'Документы в статусах, которые автоочистка отменит или скроет по истечении срока. Без учёта периода' },
    {
      label: 'На контроле', value: fmtInt(now.warnings), color: now.warnings ? WATCH_COLORS.warn : undefined,
      hint: now.watched ? `${fmtPct(share(now.warnings, now.watched))} наблюдаемых` : undefined,
      tooltip: 'Срок близок к автоочистке - документ виден во вкладке «Проблемные»'
    },
    { label: 'Критично', value: fmtInt(now.critical), color: now.critical ? WATCH_COLORS.critical : undefined, tooltip: 'До автоочистки остался день или меньше' }
  ];
}

/** Автоочистка: автоотмена и автоскрытие по дате события, ручные продления и текущая очередь */
const CleanupView: React.FC<ViewProps & { days: number; onWarnings: OnWarnings }> = ({ params, paramsKey, active, reloadToken, days, onWarnings }) => {
  const [by, setBy] = useBucketBy(days);
  const load = useLoad(
    active ? (_silent: boolean, signal: AbortSignal) => analyticsApi.cleanup(params, by, signal) : null,
    `cleanup|${by}|${paramsKey}`, reloadToken, 'Не удалось построить аналитику автоочистки'
  );
  const data = load.data;
  const stats = useMemo(() => (data ? buildStats(data) : []), [data]);

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '3fr 2fr' }, gap: 1.5 }}>
      <ChartCard
        wide title="Автоочистка и продления"
        subtitle="Столбцы - документы, которые система отменила или скрыла по истечении срока; линия - ручные продления срока. Бледный столбец - неделя, которая входит в период частично."
        loading={load.loading} error={load.error ? load.error.text : null} onRetry={load.retry}
        actions={<ByToggle value={by} onChange={setBy} />}
      >
        {!data && !load.error && <ChartSkeleton height={HEIGHT + 60} />}
        {data && <StatStrip items={stats} />}
        {data && <Chart data={data} />}
      </ChartCard>
      {data && <KindsTable data={data} onWarnings={onWarnings} />}
      {data && <OperatorsTable data={data} />}
    </Box>
  );
};

export default React.memo(CleanupView);
